import React, { useMemo } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Spin, Alert } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useConversionAnalysis } from '../hooks';

const { Title, Text } = Typography;

interface ConversionAnalysisTabProps {
  dateRange?: string;
  region?: number;
}

export const ConversionAnalysisTab: React.FC<ConversionAnalysisTabProps> = ({
  dateRange = 'all',
  region,
}) => {
  // Fetch conversion analysis data from API
  const { data, isLoading, error } = useConversionAnalysis(dateRange, region);

  // Transform API data for conversion funnel chart
  const conversionFunnel = useMemo(() => {
    if (!data?.overallStats) return [];

    const { totalQuotes, followedUpQuotes, convertedQuotes } = data.overallStats;

    return [
      {
        stage: 'Quoted',
        count: totalQuotes,
        percentage: 100
      },
      {
        stage: 'Followed Up',
        count: followedUpQuotes,
        percentage: totalQuotes > 0 ? ((followedUpQuotes / totalQuotes) * 100).toFixed(1) : 0
      },
      {
        stage: 'Ordered',
        count: convertedQuotes,
        percentage: totalQuotes > 0 ? ((convertedQuotes / totalQuotes) * 100).toFixed(1) : 0
      },
    ];
  }, [data]);

  // Transform API data to match the regional table structure
  const regionalPerformance = useMemo(() => {
    if (!data?.regionalStats) return [];

    return data.regionalStats.map(region => ({
      region: region.regionLabel,
      quotes: region.totalQuotes,
      conversion: region.conversionRate,
      convertedQuotes: region.convertedQuotes,
      revenue: 0, // Revenue data not available in the API response
    }));
  }, [data]);
  const columns = [
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      sorter: (a: { region: string }, b: { region: string }) => a.region.localeCompare(b.region, undefined, { sensitivity: 'base' }),
    },
    {
      title: 'Quotes',
      dataIndex: 'quotes',
      key: 'quotes',
      align: 'right' as const,
      sorter: (a: { quotes: number }, b: { quotes: number }) => a.quotes - b.quotes,
    },
    {
      title: 'Orders',
      dataIndex: 'convertedQuotes',
      key: 'convertedQuotes',
      align: 'right' as const,
      sorter: (a: { convertedQuotes: number }, b: { convertedQuotes: number }) => a.convertedQuotes - b.convertedQuotes,
    },
    {
      title: 'Conversion %',
      dataIndex: 'conversion',
      key: 'conversion',
      align: 'right' as const,
      sorter: (a: { conversion: number }, b: { conversion: number }) => a.conversion - b.conversion,
      render: (conversion: number) => `${conversion}%`,
    },
    {
      title: 'Performance',
      key: 'performance',
      sorter: (a: { conversion: number }, b: { conversion: number }) => a.conversion - b.conversion,
      render: (_: any, record: { conversion: number }) => (
        <Tag color={record.conversion >= 30 ? 'success' : 'warning'}>
          {record.conversion >= 30 ? 'Above Target' : 'Below Target'}
        </Tag>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card>
            <Title level={4}>Conversion Funnel</Title>
            <Text type="secondary" className="block mb-4">
              Journey from quote to order
            </Text>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '120px 0' }}>
                <Spin size="large" />
              </div>
            ) : error ? (
              <Alert
                message="Error Loading Data"
                description="Failed to load conversion funnel data."
                type="error"
                showIcon
              />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={conversionFunnel}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#22D3EE" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card>
            <Title level={4}>Key Conversion Metrics</Title>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Spin size="large" />
              </div>
            ) : error ? (
              <Alert
                message="Error Loading Data"
                description="Failed to load conversion metrics."
                type="error"
                showIcon
              />
            ) : (
              <div className="mt-6 space-y-6">
                <div>
                  <Text type="secondary">Overall Conversion Rate</Text>
                  <Title level={2} className="text-green-500">
                    {data?.overallStats.conversionRate.toFixed(2)}%
                  </Title>
                </div>
                <div>
                  <Text type="secondary">Total Quotes</Text>
                  <Title level={2}>{data?.overallStats.totalQuotes || 0}</Title>
                </div>
                <div>
                  <Text type="secondary">Lost Opportunities</Text>
                  <Title level={2} className="text-red-500">
                    {(data?.overallStats.totalQuotes || 0) - (data?.overallStats.convertedQuotes || 0)}
                  </Title>
                  <Text type="secondary" className="text-xs">
                    Quotes that never converted
                  </Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Card>
        <Title level={4} className='mb-4'>Conversion Rate by Region</Title>
        {error && (
          <Alert
            message="Error Loading Data"
            description="Failed to load regional performance data. Please try again."
            type="error"
            showIcon
            className="mb-4"
          />
        )}
        <Table
          columns={columns}
          dataSource={regionalPerformance}
          loading={isLoading}
          rowKey="region"
          pagination={false}
        />
      </Card>
    </div>
  );
};
